import { createAgreement, createCheckboxParagraph, createDateParagraph, createMotTable, createRussianTestTable, createStringParagraph, createTable, createMotivationProfileTable, createMultipleCheckboxParagraph } from '../helpers/creators'

import { motKeysArray } from '../data/motData'
import { motivationProfileKeys } from '../data/motivationProfileData'
import { cleanCheckboxArray } from './checkboxArray'

// Вспомогательные функции для обработки значений
const processInputValue = (input) => {
    if (!input) return ''
    
    const { type, value, variants } = input
    
    if (value === undefined || value === null || value === '') return ''
    
    // Обработка таблиц - возвращаем как есть (они обрабатываются отдельно)
    if (type === 'table') {
        return value
    }
    
    // Обработка чекбоксов и multiCheckbox
    if ((type === 'checkbox' || type === 'multiCheckbox') && Array.isArray(value)) {
        // Рекурсивная функция для обработки вложенных массивов
        const flattenValue = (val) => {
            if (!Array.isArray(val)) return val
            if (val.length === 0) return ''
            
            // Если первый элемент - массив, разворачиваем все уровни
            if (Array.isArray(val[0])) {
                return val.flatMap(flattenValue)
            }
            
            return val
        }
        
        const flatValue = flattenValue(value)
        
        // Теперь у нас плоский массив
        if (!Array.isArray(flatValue)) {
            return flatValue
        }
        
        // Если это индексы (числа), преобразуем через variants
        if (flatValue.every(v => typeof v === 'number')) {
            return flatValue
                .map(index => variants?.[index]?.text)
                .filter(Boolean)
                .join(', ')
        }
        
        // Если это уже текст
        if (flatValue.every(v => typeof v === 'string')) {
            return flatValue.join(', ')
        }
        
        // Смешанный тип - преобразуем все в строки
        return flatValue.map(String).join(', ')
    }
    
    // Обработка textarea
    if (type === 'textarea' && Array.isArray(value)) {
        // Разворачиваем все уровни вложенности
        const flatten = (arr) => {
            return arr.reduce((acc, val) => {
                return acc.concat(Array.isArray(val) ? flatten(val) : val)
            }, [])
        }
        
        const flatValue = flatten(value)
        return flatValue.join('\n')
    }
    
    // Обработка простых массивов
    if (Array.isArray(value)) {
        // Разворачиваем вложенные массивы
        const flatten = (arr) => {
            return arr.reduce((acc, val) => {
                return acc.concat(Array.isArray(val) ? flatten(val) : val)
            }, [])
        }
        
        const flatValue = flatten(value)
        
        // Если все элементы - строки, объединяем
        if (flatValue.every(v => typeof v === 'string')) {
            return flatValue.join(', ')
        }
        
        // Иначе преобразуем в строки и объединяем
        return flatValue.map(String).join(', ')
    }
    
    // Простое значение
    if (typeof value === 'string') return value
    
    return String(value)
}

// Конфигурация Directus
const DIRECTUS_CONFIG = {
    baseUrl: 'https://crm-hr.kzn.ru/',
    token: 'sGaLAYCUxT_HpfAaMiQNb4qzieuAtUAM',
    flows: {
        motivation: '89a9d812-e6d3-4134-abc8-d052c0643175',    // Мотивация на деятельность
        experience: 'f6c5bd23-6cdb-4e72-959d-6d12a834e468',    // Анализ деятельности
        anketa1: 'daff58a7-6e27-4c3d-8ab7-7e77c5a654ff',       // Анкета №1
        director: 'f726b855-d370-48a2-9050-0c41c3a27e8d',      // Анкета руководителя
    }
}

// Функция для отправки данных в Directus
const sendToDirectus = async (data, flowType) => {
    const flowId = DIRECTUS_CONFIG.flows[flowType]
    
    if (!flowId) {
        console.error(`Flow type "${flowType}" не найден в конфигурации`)
        return
    }

    try {
        console.log(`Отправка данных в Flow ${flowType}:`, JSON.stringify(data, null, 2))
        
        const response = await fetch(
            `${DIRECTUS_CONFIG.baseUrl}flows/trigger/${flowId}`,
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${DIRECTUS_CONFIG.token}`,
                },
                body: JSON.stringify(data),
            }
        )

        // Получаем текст ответа для отладки
        const responseText = await response.text()
        console.log(`Ответ сервера (${response.status}):`, responseText)

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}, body: ${responseText}`)
        }

        // Пытаемся распарсить JSON, если ответ не пустой
        let result
        if (responseText.trim()) {
            try {
                result = JSON.parse(responseText)
            } catch (e) {
                console.warn('Не удалось распарсить JSON ответ, используем текст:', responseText)
                result = { message: responseText }
            }
        } else {
            result = { message: 'Success (empty response)' }
        }

        console.log(`Данные успешно отправлены в Directus (${flowType}):`, result)
        return result
    } catch (error) {
        console.error(`Ошибка при отправке данных в Directus (${flowType}):`, error)
        throw error
    }
}

// Словарь соответствий ключей мотивационного профиля → поля Directus
const directusKeyMap = {
  "Высокий заработок": "high_earning",
  "Физические условия работы": "physical_work",
  "Четкое структурирование работы": "need_of_instructions",
  "Социальные контакты": "social_contacts",
  "Взаимоотношения": "relationships",
  "Признание": "recognition",
  "Стремление к достижениям": "will_of_achievements",
  "Власть и влияние": "power_and_inference",
  "Разнообразие и перемены": "variety_and_change",
  "Креативность": "creativity",
  "Самосовершенствование": "self_improvement",
  "Интересная и полезная работа": "interesting_work"
}

// Порядок факторов 1..12 → точные имена полей в Directus
const directusFieldsByIndex = [
  'high_earning',
  'physical_work',
  'need_of_instructions',
  'social_contacts',
  'relationships',
  'recognition',
  'will_of_achievements',
  'power_and_inference',
  'variety_and_change',
  'creativity',
  'self_improvement',
  'interesting_work',
]

// Маппинг числовых ключей результата (1..12) на поля Directus
const mapResultToDirectusFields = (result, name) => {
  const mapped = { fio: name || '' }

  for (let i = 1; i <= 12; i++) {
    const field = directusFieldsByIndex[i - 1]
    mapped[field] = Number(result[i]) || 0
  }

  return mapped
}

const filterCheckboxInputs = (inputs) => {
    return inputs.filter(input => input.type === 'checkbox')
}

const getNameValue = (inputs) => {
    const nameInput = inputs.filter(input => input.id === 0)
    return nameInput[0]?.value || ''
}

export const getVariant = (inputs) => {
    const filtered = inputs.filter(input => input.id === 'variant')
    return filtered[0]?.variant
}

export const createMark = (rightAnswers, filtered) => {
    const percentage = rightAnswers / filtered.length

    if (percentage === 1) {
        return 10
    } else if (percentage >= 0.81 && percentage <= 0.9) {
        return 9
    } else if (percentage === 0.8) {
        return 8
    } else if (percentage >= 0.7 && percentage <= 0.79) {
        return 7
    } else if (percentage >= 0.61 && percentage <= 0.69) {
        return 6
    } else if (percentage === 0.6) {
        return 5
    } else if (percentage >= 0.5 && percentage <= 0.59) {
        return 4
    } else if (percentage >= 0.41 && percentage <= 0.49) {
        return 3
    } else if (percentage <= 0.40) {
        return 2
    }
}

export const motAnalysis = (inputs) => {
    const filtered = filterCheckboxInputs(inputs)
    const name = getNameValue(inputs)
    let result = [0, 0, 0, 0, 0]

    for (let input of filtered) {
        if (input?.isCalculable) {
            let answers = cleanCheckboxArray(input.value)
            let keys = motKeysArray[input.id]

            for (let a = 0; a < answers.length; a++) {
                let answer = answers[a]

                for (let k = 0; k < keys.length; k++) {
                    if (keys[k].includes(answer + 1)) {
                        result[k] = result[k] + 1
                    }
                }
            }
        }
    }

    const resultString = createMotTable(result)

    return {
        name,
        result: resultString,
    }
}

export const russianTestAnalysis = (inputs) => {
    const filtered = filterCheckboxInputs(inputs)
    const name = getNameValue(inputs)
    const results = {}

    let rightAnswers = filtered.length

    for (let input of filtered) {
        if (input?.isCheckable) {
            let object
            const value = cleanCheckboxArray(input.value)
            if (value.length !== 0) {
                const usersAnswerId = value[0]
                const usersAnswerText = input.variants[usersAnswerId].text

                object = {
                    variant: usersAnswerText,
                    isRight: true,
                }

                if (!input.answers.includes(usersAnswerId)) {
                    object.isRight = false
                    rightAnswers--
                }
            } else {
                object = {
                    variant: '',
                    isRight: false,
                }

                rightAnswers--
            }

            results[input.id] = object
        }
    }

    const variant = getVariant(inputs)
    const mark = createMark(rightAnswers, filtered)

    const resultString = createRussianTestTable(variant, results, rightAnswers, mark)

    return {
        name,
        result: resultString,
    }
}

const getMotivationAnswers = (inputs) => {
    const filtered = inputs.filter(input => input.type === 'motivation')
    return filtered[0]?.value
}

// Анализ мотивационного профиля с сохранением в Directus
export const motivationProfileAnalysis = (inputs) => {
    const name = getNameValue(inputs)
    const answers = getMotivationAnswers(inputs)
    const result = {}

    console.log('Входящие данные:', inputs)
    console.log('Имя:', name)
    console.log('Ответы:', answers)

    // Подсчитываем результаты по каждой категории
    for (let i = 0; i < Object.keys(answers).length; i++) {
        const currentAnswers = answers[i]
        const currentKeys = motivationProfileKeys[i]

        for (let k = 0; k < currentKeys.length; k++) {
            const currentAnswer = currentAnswers[k]
            const currentKey = currentKeys[k]

            if (result[currentKey] === undefined) {
                result[currentKey] = 0
            }

            result[currentKey] += Number(currentAnswer)
        }
    }

    console.log('Финальный результат подсчета:', result)

    const resultString = createMotivationProfileTable(result)
    const data_for_directus = mapResultToDirectusFields(result, name)
    
    // Добавляем дополнительные поля для Motivation Profile
    data_for_directus.name = name
    data_for_directus.test = "Тест мотивация"
    data_for_directus.result = resultString
    data_for_directus.pdf_url = "" // Можно добавить генерацию PDF позже
    
    console.log('data_for_directus:', data_for_directus)

    // Отправляем в Flow мотивационного профиля
    sendToDirectus(data_for_directus, 'motivation')
        .then(() => {
            console.log('Результаты мотивационного профиля сохранены в Directus')
        })
        .catch((error) => {
            console.error('Не удалось сохранить результаты в Directus:', error)
        })

    return {
        name,
        result: resultString,
        data_for_directus
    }
}

// Анализ анкеты опыта работы с отправкой в Directus
export const experienceAnalysis = (inputs) => {
    const getValueById = (id) => {
        const row = inputs.find((x) => x.id === id)
        return row?.value || ''
    }

    const employee_full_name = getValueById(0)
    const main_responsibilities = getValueById(1)
    const achieved_results = getValueById(2)
    const information_sources = getValueById(3)
    const strengths_used = getValueById(4)
    const obstacles_faced = getValueById(5)
    const obstacles_causes = getValueById(6)
    const situation_development_outlook = getValueById(8)
    const improvement_plan = getValueById(9)

    // Таблица участников (id:7)
    const tableRow = inputs.find((x) => x.id === 7)
    const tableValue = Array.isArray(tableRow?.value) ? tableRow.value : []

    const interaction_partners = tableValue
        .filter((row) => Array.isArray(row) && (row[0] || row[1]))
        .map((row) => ({
            partner_name: row[0] || '',
            interaction_topics: row[1] || '',
        }))

    const dataForDirectus = {
        employee_full_name,
        main_responsibilities,
        achieved_results,
        information_sources,
        strengths_used,
        obstacles_faced,
        obstacles_causes,
        situation_development_outlook,
        improvement_plan,
        interaction_partners,
    }

    // Формируем HTML-результат
    let resultString = '<div>'
    for (let i = 0; i < inputs.length; i++) {
        const current = inputs[i]
        const currentType = current.type

        if (currentType === 'bottomInput' || currentType === 'simpleInput' || currentType === 'textarea' || currentType === 'select') {
            resultString += createStringParagraph(current)
        }

        if (currentType === 'checkbox') {
            resultString += createCheckboxParagraph(current)
        }

        if (currentType === 'multiCheckbox') {
            resultString += createMultipleCheckboxParagraph(current)
        }

        if (currentType === 'table') {
            resultString += createTable(current)
        }

        if (currentType === 'checkboxAgreement') {
            resultString += createAgreement(current)
        }
    }
    const dateParagraph = createDateParagraph()
    resultString += `${dateParagraph}</div>`

    // Отправляем через Flow для анализа деятельности
    sendToDirectus(dataForDirectus, 'experience')
        .then(() => {
            console.log('Анкета опыта сохранена в Directus')
        })
        .catch((error) => {
            console.error('Не удалось сохранить анкету опыта в Directus:', error)
        })

    return {
        name: employee_full_name,
        result: resultString,
        data_for_directus: dataForDirectus,
    }
}

// Анализ Анкеты №1 с отправкой в Directus
export const anketa1Analysis = (inputs) => {
    // Универсальная функция получения значения с обработкой разных типов
    const getValueById = (id) => {
        const input = inputs.find((x) => x.id === id)
        return processInputValue(input)
    }

    // Извлекаем таблицы (массивы объектов)
    const getTableById = (id) => {
        const row = inputs.find((x) => x.id === id)
        if (!row) return []
        
        const tableValue = Array.isArray(row?.value) ? row.value : []
        return tableValue.filter((r) => Array.isArray(r) && r.some(cell => cell))
    }

    // Основные поля - ИСПРАВЛЕННЫЕ ID на основе ваших логов
    const full_name = getValueById(0)
    const birth_date = getValueById(1)
    const marital_status = getValueById(2)
    const children_info = getValueById(3)
    const birth_place = getValueById(4)
    const registration = getValueById(5)
    const residence_address = getValueById(6)
    const contact_phones = getValueById(7)
    const email = getValueById(8)
    const activity_direction = getValueById(9)
    const desired_position = getValueById(10)
    const min_salary = getValueById(11)
    const vacancy_source = getValueById(12)
    
    // Таблицы с ПРАВИЛЬНЫМИ названиями полей для Directus
    const education_records = getTableById(13).map(row => ({
        start_end_year: row[1] || '',        // Года обучения
        institution: row[0] || '',            // Учебное заведение
        faculty_specialty: row[2] || '',      // Факультет/специальность
        education_level: row[3] || '',        // Уровень образования (диплом)
        city: row[4] || ''                    // Город
    }))
    
    const school_education = getTableById(14).map(row => ({
        school_name: row[0] || '',            // Название школы
        start_end_year: row[1] || '',         // Года обучения
        city: row[2] || ''                    // Город
    }))
    
    const military_status = getValueById(15)
    
    const work_experience = getTableById(16).map(row => ({
        period: row[0] || '',                 // Период работы
        position_workplace: row[1] || '',     // Должность, место работы
        city: row[2] || '',                   // Город
        reason_for_change: row[3] || ''       // Причина смены работы
    }))
    
    const job_duties = getValueById(17)
    
    const additional_info = getTableById(18).map(row => ({
        computer_skills: row[0] || '',        // Курсы/навыки
        drivers_license_car: row[1] || '',    // Водительское удостоверение/авто
        criminal_record: row[2] || ''         // Судимость
    }))
    
    // ИСПРАВЛЕНО: id 19 - это multiCheckbox языков
    // multiCheckbox возвращает строки вида "0.2", "2.1" - первое число = язык, второе = уровень
    const languagesInput = inputs.find(x => x.id === 19)
    console.log('Languages input (id=19):', languagesInput)
    console.log('Languages variants:', languagesInput?.variants)
    
    let languages = []
    if (languagesInput && languagesInput.value) {
        const langValue = languagesInput.value
        console.log('Languages value:', langValue)
        console.log('Languages value type:', typeof langValue, Array.isArray(langValue))
        
        if (Array.isArray(langValue)) {
            langValue.forEach((val, index) => {
                console.log(`Processing language value [${index}]:`, val, typeof val)
                
                if (val && typeof val === 'string' && val.includes('.')) {
                    const [langIndex, levelIndex] = val.split('.').map(Number)
                    console.log(`  Parsed: langIndex=${langIndex}, levelIndex=${levelIndex}`)
                    
                    // Получаем название языка
                    const languageName = languagesInput.variants?.[langIndex]?.text || ''
                    console.log(`  Language name:`, languageName)
                    
                    // Получаем уровень владения
                    let proficiencyLevel = ''
                    const variant = languagesInput.variants?.[langIndex]
                    console.log(`  Variant structure:`, variant)
                    
                    if (variant) {
                        // Вариант 1: вложенные variants (например для чекбоксов с подвариантами)
                        if (variant.variants && Array.isArray(variant.variants) && variant.variants[levelIndex]) {
                            proficiencyLevel = variant.variants[levelIndex].text || variant.variants[levelIndex] || ''
                            console.log(`  Found level in variants:`, proficiencyLevel)
                        }
                        // Вариант 2: levels как отдельное поле
                        else if (variant.levels && Array.isArray(variant.levels) && variant.levels[levelIndex]) {
                            proficiencyLevel = variant.levels[levelIndex].text || variant.levels[levelIndex] || ''
                            console.log(`  Found level in levels:`, proficiencyLevel)
                        }
                        // Вариант 3: options
                        else if (variant.options && Array.isArray(variant.options) && variant.options[levelIndex]) {
                            proficiencyLevel = variant.options[levelIndex].text || variant.options[levelIndex] || ''
                            console.log(`  Found level in options:`, proficiencyLevel)
                        }
                        // Вариант 4: subVariants
                        else if (variant.subVariants && Array.isArray(variant.subVariants) && variant.subVariants[levelIndex]) {
                            proficiencyLevel = variant.subVariants[levelIndex].text || variant.subVariants[levelIndex] || ''
                            console.log(`  Found level in subVariants:`, proficiencyLevel)
                        }
                    }
                    
                    // Если уровень не найден в структуре, используем стандартные уровни по индексу
                    if (!proficiencyLevel && !isNaN(levelIndex)) {
                        const standardLevels = ['A1 (начальный)', 'A2 (элементарный)', 'B1 (средний)', 'B2 (выше среднего)', 'C1 (продвинутый)', 'C2 (свободное владение)']
                        proficiencyLevel = standardLevels[levelIndex] || `Уровень ${levelIndex + 1}`
                        console.log(`  Using standard level:`, proficiencyLevel)
                    }
                    
                    if (languageName) {
                        languages.push({
                            language_name: languageName,
                            proficiency_level: proficiencyLevel
                        })
                        console.log(`  Added language:`, { language_name: languageName, proficiency_level: proficiencyLevel })
                    }
                }
            })
        }
        
        languages = languages.filter(lang => lang.language_name && lang.language_name.trim())
    }
    
    console.log('Processed languages (final):', languages)
    
    const family_relatives = getTableById(20).map(row => ({
        relationship: row[0] || '',           // Степень родства
        full_name: row[1] || '',              // ФИО
        birth_date: row[2] || '',             // Год рождения
        work_position: row[3] || '',          // Место работы
        residence: row[4] || ''               // Место жительства
    }))
    
    const government_relatives = getTableById(21).map(row => ({
        full_name: row[0] || '',              // ФИО
        relationship: row[1] || '',           // Степень родства
        birth_date: row[2] || '',             // Год рождения (если есть)
        position: row[3] || '',               // Должность
        workplace: row[4] || ''               // Место работы
    }))
    
    const development_period = getValueById(22)
    const work_attractiveness = getValueById(23)
    const motivation_factors = getValueById(24)
    const why_hire_you = getValueById(25)
    const professional_areas = getValueById(26)
    const executive_qualities = getValueById(27)
    const manager_qualities = getValueById(28)
    const manager_flaws = getValueById(29)
    const kazan_attraction = getValueById(30)
    const negotiation_strategies = getValueById(31)
    const leadership_conditions = getValueById(32)
    const standpoint_conditions = getValueById(33)
    const career_plans = getValueById(34)
    const hobbies = getValueById(35)
    const sports = getValueById(36)
    const cybersports = getValueById(37)

    const dataForDirectus = {
        fio: full_name,                                      // ✅ Исправлено: full_name → fio
        date_of_birth: birth_date,                           // ✅ Исправлено: birth_date → date_of_birth
        marital_status: marital_status,
        children: children_info,                             // ✅ Исправлено: children_info → children
        birth_place: birth_place,                            // Добавлено (не было в JSON)
        registration: registration,
        residence: residence_address,                        // ✅ Исправлено: residence_address → residence
        phone_number: contact_phones,                        // ✅ Исправлено: contact_phones → phone_number
        email: email,
        interest_area: activity_direction,                   // ✅ Исправлено: activity_direction → interest_area
        desired_position: desired_position,
        min_salary: min_salary,
        vacancy_source: vacancy_source,
        military_status: military_status,                    // Добавлено (не было в JSON, но есть в структуре)
        responsibilities: job_duties,                        // ✅ Исправлено: job_duties → responsibilities
        most_development_contribution: development_period,   // ✅ Исправлено: development_period → most_development_contribution
        what_attracts: work_attractiveness,                  // ✅ Исправлено: work_attractiveness → what_attracts
        work_motivation: motivation_factors,                 // ✅ Исправлено: motivation_factors → work_motivation
        why_us: why_hire_you,                                // ✅ Исправлено: why_hire_you → why_us
        expertise_areas: professional_areas,                 // ✅ Исправлено: professional_areas → expertise_areas
        executor_traits: executive_qualities,                // ✅ Исправлено: executive_qualities → executor_traits
        leader_traits: manager_qualities,                    // ✅ Исправлено: manager_qualities → leader_traits
        leader_flaws: manager_flaws,                         // ✅ Исправлено: manager_flaws → leader_flaws
        what_attracts_kazan_executive: kazan_attraction,     // ✅ Исправлено: kazan_attraction → what_attracts_kazan_executive
        negotiation_strategies: negotiation_strategies,
        lead_condition: leadership_conditions,               // ✅ Исправлено: leadership_conditions → lead_condition
        defend_before_superior: standpoint_conditions,       // ✅ Исправлено: standpoint_conditions → defend_before_superior
        professional_plans: career_plans,                    // ✅ Исправлено: career_plans → professional_plans
        hobbies: hobbies,
        sports: sports,
        esports_games: cybersports,                          // ✅ Исправлено: cybersports → esports_games
        education_records,
        school_education,
        work_experience,
        additional_info,
        family_relatives,
        government_relatives,
        languages
    }

    // Логирование для отладки
    console.log('=== Anketa1 Debug ===')
    console.log('Raw inputs:', inputs)
    
    // Проверяем проблемные поля
    console.log('\nПроверка таблиц:')
    console.log('education_records (id=13):', inputs.find(x => x.id === 13))
    console.log('education_records обработано:', education_records)
    
    console.log('\nschool_education (id=14):', inputs.find(x => x.id === 14))
    console.log('school_education обработано:', school_education)
    
    console.log('\nwork_experience (id=16):', inputs.find(x => x.id === 16))
    console.log('work_experience обработано:', work_experience)
    
    console.log('\nПроверка простых полей:')
    console.log('military_status (id=15):', military_status)
    console.log('job_duties (id=17):', job_duties)
    
    console.log('\nВсе обработанные данные:')
    console.log('Processed data:', dataForDirectus)
    console.log('====================\n')

    // Формируем HTML-результат
    let resultString = '<div>'
    for (let i = 0; i < inputs.length; i++) {
        const current = inputs[i]
        const currentType = current.type

        if (currentType === 'bottomInput' || currentType === 'simpleInput' || currentType === 'textarea' || currentType === 'select') {
            resultString += createStringParagraph(current)
        }

        if (currentType === 'checkbox') {
            resultString += createCheckboxParagraph(current)
        }

        if (currentType === 'multiCheckbox') {
            resultString += createMultipleCheckboxParagraph(current)
        }

        if (currentType === 'table') {
            resultString += createTable(current)
        }

        if (currentType === 'checkboxAgreement') {
            resultString += createAgreement(current)
        }
    }
    const dateParagraph = createDateParagraph()
    resultString += `${dateParagraph}</div>`

    // Отправляем через Flow для Анкеты №1
    sendToDirectus(dataForDirectus, 'anketa1')
        .then(() => {
            console.log('Анкета №1 сохранена в Directus')
        })
        .catch((error) => {
            console.error('Не удалось сохранить Анкету №1 в Directus:', error)
        })

    return {
        name: full_name,
        result: resultString,
        data_for_directus: dataForDirectus,
    }
}

// Анализ Анкеты руководителя с отправкой в Directus
// Анализ Анкеты руководителя с отправкой в Directus
// Анализ Анкеты руководителя с отправкой в Directus
export const directorAnalysis = (inputs) => {
    // Универсальная функция получения значения с обработкой разных типов
    const getValueById = (id) => {
        const input = inputs.find((x) => x.id === id)
        return processInputValue(input)
    }

    const getTableById = (id) => {
        const row = inputs.find((x) => x.id === id)
        if (!row) return []
        
        const tableValue = Array.isArray(row?.value) ? row.value : []
        return tableValue.filter((r) => Array.isArray(r) && r.some(cell => cell))
    }

    const getCheckboxById = (id) => {
        const input = inputs.find((x) => x.id === id)
        return processInputValue(input)
    }

    // Основные поля - ID соответствуют реальной структуре формы Director
    const full_name = getValueById(0)
    const birth_date = getValueById(1)
    const birth_place = getValueById(2)
    const marital_status = getValueById(3)
    const children_info = getValueById(4)
    const registration_address = getValueById(5)
    const residence_address = getValueById(6)
    const contact_phone = getValueById(7)
    const desired_position_ = getValueById(8)
    const min_salary = getValueById(9)
    const vacancy_source = getValueById(10)
    const military_status = getValueById(11)

    // Таблица образования (id=12)
    const education = getTableById(12).map(row => ({
        start_end_year: row[1] || '',
        institution: row[0] || '',
        faculty_specialty: row[2] || '',
        education_level: row[3] || '',
        city: row[4] || ''
    }))
    
    // Таблица школ (id=13)
    const schools = getTableById(13).map(row => ({
        school_name: row[0] || '',
        start_end_year: row[1] || '',
        city: row[2] || ''
    }))
    
    // Таблица опыта работы (id=14)
    const work_experience = getTableById(14).map(row => ({
        period: row[0] || '',
        position_workplace: row[1] || '',
        city: row[2] || '',
        reason_for_change: row[3] || ''
    }))

    // ТЕКСТАРЕА поля
    const job_duties = getValueById(15)
    
    // Таблица дополнительной информации (id=16)
    const additional_info = getTableById(16).map(row => ({
        computer_skills: row[0] || '',
        drivers_license_car: row[1] || '',
        criminal_record: row[2] || ''
    }))
    
    // Обработка языков (id=17 - multiCheckbox)
    const languagesInput = inputs.find(x => x.id === 17)
    console.log('Director Languages input (id=17):', languagesInput)
    console.log('Director Languages variants:', languagesInput?.variants)
    
    let languages = []
    if (languagesInput && languagesInput.value) {
        const langValue = languagesInput.value
        console.log('Director Languages value:', langValue)
        console.log('Director Languages value type:', typeof langValue, Array.isArray(langValue))
        
        if (Array.isArray(langValue)) {
            langValue.forEach((val, index) => {
                console.log(`Processing language value [${index}]:`, val, typeof val)
                
                if (val && typeof val === 'string' && val.includes('.')) {
                    const [langIndex, levelIndex] = val.split('.').map(Number)
                    console.log(`  Parsed: langIndex=${langIndex}, levelIndex=${levelIndex}`)
                    
                    // Получаем название языка
                    const languageName = languagesInput.variants?.[langIndex]?.text || ''
                    console.log(`  Language name:`, languageName)
                    
                    // Получаем уровень владения
                    let proficiencyLevel = ''
                    const variant = languagesInput.variants?.[langIndex]
                    console.log(`  Variant structure:`, variant)
                    
                    if (variant) {
                        // Вариант 1: вложенные variants
                        if (variant.variants && Array.isArray(variant.variants) && variant.variants[levelIndex]) {
                            proficiencyLevel = variant.variants[levelIndex].text || variant.variants[levelIndex] || ''
                            console.log(`  Found level in variants:`, proficiencyLevel)
                        }
                        // Вариант 2: levels как отдельное поле
                        else if (variant.levels && Array.isArray(variant.levels) && variant.levels[levelIndex]) {
                            proficiencyLevel = variant.levels[levelIndex].text || variant.levels[levelIndex] || ''
                            console.log(`  Found level in levels:`, proficiencyLevel)
                        }
                        // Вариант 3: options
                        else if (variant.options && Array.isArray(variant.options) && variant.options[levelIndex]) {
                            proficiencyLevel = variant.options[levelIndex].text || variant.options[levelIndex] || ''
                            console.log(`  Found level in options:`, proficiencyLevel)
                        }
                        // Вариант 4: subVariants
                        else if (variant.subVariants && Array.isArray(variant.subVariants) && variant.subVariants[levelIndex]) {
                            proficiencyLevel = variant.subVariants[levelIndex].text || variant.subVariants[levelIndex] || ''
                            console.log(`  Found level in subVariants:`, proficiencyLevel)
                        }
                    }
                    
                    // Если уровень не найден в структуре, используем стандартные уровни по индексу
                    if (!proficiencyLevel && !isNaN(levelIndex)) {
                        const standardLevels = ['A1 (начальный)', 'A2 (элементарный)', 'B1 (средний)', 'B2 (выше среднего)', 'C1 (продвинутый)', 'C2 (свободное владение)']
                        proficiencyLevel = standardLevels[levelIndex] || `Уровень ${levelIndex + 1}`
                        console.log(`  Using standard level:`, proficiencyLevel)
                    }
                    
                    if (languageName) {
                        languages.push({
                            language_name: languageName,
                            proficiency_level: proficiencyLevel
                        })
                        console.log(`  Added language:`, { language_name: languageName, proficiency_level: proficiencyLevel })
                    }
                }
            })
        }
        
        languages = languages.filter(lang => lang.language_name && lang.language_name.trim())
    }
    
    console.log('Director Processed languages (final):', languages)
    
    // Таблица семьи (id=18)
    const family = getTableById(18).map(row => ({
        relationship: row[0] || '',
        full_name: row[1] || '',
        birth_date: row[2] || '',
        work_position: row[3] || '',
        residence: row[4] || ''
    }))
    
    // Таблица государственных родственников (id=19)
    const government_relatives = getTableById(19).map(row => ({
        full_name: row[0] || '',
        relationship: row[1] || '',
        birth_date: row[2] || '',
        position: row[3] || '',
        workplace: row[4] || ''
    }))

    // ОСТАЛЬНЫЕ ТЕКСТАРЕА поля
    const personal_achievements = getValueById(20)
    const project_participation = getValueById(21)
    const professional_areas = getValueById(22)
    const advantages = getValueById(23)
    const motivation_factors = getValueById(24)
    const subordinates_count = getValueById(25)
    const manager_attraction = getValueById(26)

    // ЧЕКБОКСЫ для переговоров, условий и позиции
    const negotiation_strategies = getCheckboxById(27)
    const leadership_conditions = getCheckboxById(28)
    const standpoint_conditions = getCheckboxById(29)

    // ФИНАЛЬНЫЕ ТЕКСТАРЕА
    const career_plans = getValueById(30)
    const hobbies = getValueById(31)
    const sports = getValueById(32)
    const cybersports = getValueById(33)

    // Объект данных для Directus - ВСЕ поля соответствуют схеме
    const dataForDirectus = {
        full_name,
        birth_date,
        birth_place,
        marital_status,
        children_info,
        registration_address,
        residence_address,
        contact_phone,
        desired_position_,
        min_salary,
        vacancy_source,
        military_status,
        job_duties,
        personal_achievements,
        project_participation,
        professional_areas,
        advantages,
        motivation_factors,
        subordinates_count,
        manager_attraction,
        negotiation_strategies,
        leadership_conditions,
        standpoint_conditions,
        career_plans,
        hobbies,
        sports,
        cybersports,
        education,
        schools,
        work_experience,
        additional_info,
        languages,
        family,
        government_relatives
    }

    // Логирование для отладки
    console.log('=== Director Analysis Debug ===')
    console.log('Raw inputs:', inputs)
    
    console.log('\nПроверка таблиц:')
    console.log('education (id=12):', inputs.find(x => x.id === 12))
    console.log('education обработано:', education)
    
    console.log('\nschools (id=13):', inputs.find(x => x.id === 13))
    console.log('schools обработано:', schools)
    
    console.log('\nwork_experience (id=14):', inputs.find(x => x.id === 14))
    console.log('work_experience обработано:', work_experience)
    
    console.log('\nadditional_info (id=16):', inputs.find(x => x.id === 16))
    console.log('additional_info обработано:', additional_info)
    
    console.log('\nfamily (id=18):', inputs.find(x => x.id === 18))
    console.log('family обработано:', family)
    
    console.log('\ngovernment_relatives (id=19):', inputs.find(x => x.id === 19))
    console.log('government_relatives обработано:', government_relatives)
    
    console.log('\nПроверка текстареа и других полей:')
    console.log('job_duties (id=15):', job_duties)
    console.log('personal_achievements (id=20):', personal_achievements)
    console.log('project_participation (id=21):', project_participation)
    console.log('professional_areas (id=22):', professional_areas)
    console.log('advantages (id=23):', advantages)
    console.log('motivation_factors (id=24):', motivation_factors)
    console.log('subordinates_count (id=25):', subordinates_count)
    console.log('manager_attraction (id=26):', manager_attraction)
    console.log('negotiation_strategies (id=27):', negotiation_strategies)
    console.log('leadership_conditions (id=28):', leadership_conditions)
    console.log('standpoint_conditions (id=29):', standpoint_conditions)
    console.log('career_plans (id=30):', career_plans)
    console.log('hobbies (id=31):', hobbies)
    console.log('sports (id=32):', sports)
    console.log('cybersports (id=33):', cybersports)
    
    console.log('\nВсе обработанные данные:')
    console.log('Processed data:', dataForDirectus)
    console.log('====================\n')

    // Формируем HTML-результат
    let resultString = '<div>'
    for (let i = 0; i < inputs.length; i++) {
        const current = inputs[i]
        const currentType = current.type

        if (currentType === 'bottomInput' || currentType === 'simpleInput' || currentType === 'textarea' || currentType === 'select') {
            resultString += createStringParagraph(current)
        }

        if (currentType === 'checkbox') {
            resultString += createCheckboxParagraph(current)
        }

        if (currentType === 'multiCheckbox') {
            resultString += createMultipleCheckboxParagraph(current)
        }

        if (currentType === 'table') {
            resultString += createTable(current)
        }

        if (currentType === 'checkboxAgreement') {
            resultString += createAgreement(current)
        }
    }
    const dateParagraph = createDateParagraph()
    resultString += `${dateParagraph}</div>`

    // Отправляем через Flow для Анкеты руководителя
    sendToDirectus(dataForDirectus, 'director')
        .then(() => {
            console.log('Анкета руководителя сохранена в Directus')
        })
        .catch((error) => {
            console.error('Не удалось сохранить Анкету руководителя в Directus:', error)
        })

    return {
        name: full_name,
        result: resultString,
        data_for_directus: dataForDirectus,
    }
}

// Универсальная функция для обратной совместимости
export const formAnalysis = (inputs) => {
    let resultString = '<div>'

    for (let i = 0; i < inputs.length; i++) {
        const current = inputs[i]
        const currentType = current.type

        if (currentType === 'bottomInput' || currentType === 'simpleInput' || currentType === 'textarea' || currentType === 'select') {
            resultString += createStringParagraph(current)
        }

        if (currentType === 'checkbox') {
            resultString += createCheckboxParagraph(current)
        }

        if (currentType === 'multiCheckbox') {
            resultString += createMultipleCheckboxParagraph(current)
        }

        if (currentType === 'table') {
            resultString += createTable(current)
        }

        if (currentType === 'checkboxAgreement') {
            resultString += createAgreement(current)
        }
    }

    const date = createDateParagraph()
    resultString += `${date}</div>`

    const nameInput = inputs.find(input => input.id === 0)

    return {
        name: nameInput?.value || '',
        result: resultString,
    }
}